import { useMutation } from '@apollo/client'
import { TitledPaper } from '@dai/components'
import { Image as ImageIcon, SkipNext } from '@mui/icons-material'
import { Alert, Box, Button } from '@mui/material'
import { logAnalyticsEvent } from 'api/Firebase'
import { useCallback, useEffect, useState } from 'react'

import {
  CREATE_TENANT_LOGO_IMAGE,
  CreateTenantLogoImageInput,
  CreateTenantLogoImageMutation,
  CreateTenantLogoImageMutationVariables,
  GET_LOGGED_IN_USER,
  GetLoggedInUserQuery,
} from '@/graphql'
import useT from '@/i18n/useT'
import PhotoUpload from '@/pages/forms/components/PhotoUpload'
import { uploadImageAndGetData } from '@/utils/image.helpers'

import { AccountSetUpStepProps } from '../types'

function CompanySetUp({ nextStep, user }: AccountSetUpStepProps) {
  const { t } = useT()
  const [image, setImage] = useState<any | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (user?.tenant?.logoImage?.url) {
      setImagePreview(user.tenant.logoImage.url)
    }
  }, [user?.tenant?.logoImage?.url])

  const [createTenantLogoImage] = useMutation<
    CreateTenantLogoImageMutation,
    CreateTenantLogoImageMutationVariables
  >(CREATE_TENANT_LOGO_IMAGE)

  const handleSubmit = useCallback(async () => {
    try {
      setSubmitting(true)
      if (image) {
        if (!user) {
          throw new Error(t('company.setUp.userNotFound'))
        } else if (!user.tenant) {
          throw new Error(t('company.setUp.notAssignedToTenant'))
        }

        const { bucketFileName, url } = await uploadImageAndGetData({
          image,
          folder: 'company-logos',
        })

        const imageData: CreateTenantLogoImageInput = {
          bucketFileName,
          url,
          tenant: { set: user.tenant.id },
        }

        await createTenantLogoImage({
          variables: {
            data: imageData,
          },
          update: (cache, { data }) => {
            if (data?.createTenantLogoImage) {
              const existingData = cache.readQuery<GetLoggedInUserQuery>({
                query: GET_LOGGED_IN_USER,
              })

              if (existingData?.loggedInUser) {
                cache.writeQuery({
                  query: GET_LOGGED_IN_USER,
                  data: {
                    loggedInUser: {
                      ...existingData.loggedInUser,
                      tenant: {
                        ...existingData.loggedInUser.tenant,
                        logoImage: data.createTenantLogoImage,
                      },
                    },
                  },
                })
              }
            }
          },
        })
      }

      logAnalyticsEvent('set_up_company_complete')
      nextStep()
    } catch (error: any) {
      setErrorMessage(error.message || t('company.setUp.errorUploading'))
    } finally {
      setSubmitting(false)
    }
  }, [
    createTenantLogoImage,
    nextStep,
    user,
    setSubmitting,
    image,
    setErrorMessage,
    t,
  ])

  return (
    <TitledPaper
      title={t('company.setUp.companyLogo')}
      subtitle={t('company.setUp.uploadLogo')}
    >
      {errorMessage && (
        <Alert sx={{ mt: 2, mb: 1 }} severity="warning">
          {errorMessage}
        </Alert>
      )}
      <PhotoUpload
        container="fit"
        setImage={setImage}
        imagePreview={imagePreview}
        setImagePreview={setImagePreview}
        Button={
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ImageIcon />}
            fullWidth
            component="span" // important for some reason
          >
            {imagePreview
              ? t('company.setUp.changePhoto')
              : t('company.setUp.uploadPhoto')}
          </Button>
        }
      />
      <Box sx={{ display: 'flex', justifyContent: 'row', gap: 4 }}>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          loading={submitting}
          loadingPosition="end"
          disabled={!imagePreview}
          onClick={handleSubmit}
        >
          {t('common.continue')}
        </Button>
        {!imagePreview && (
          <Button
            variant="outlined"
            color="secondary"
            fullWidth
            onClick={nextStep}
            endIcon={<SkipNext />}
          >
            {t('common.skipForNow')}
          </Button>
        )}
      </Box>
    </TitledPaper>
  )
}

export default CompanySetUp
